import { CheckCircleOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { type DefaultTheme, useTheme } from 'styled-components';

import UPDATE_NOTIFICATIONS from 'components/Notifications/queries/UpdateNotifications.gql';
import type { AtlasGqlNotification } from 'types/atlas-graphql';
import { useMutation } from 'utils/apollo';

import {
  ContentHeader,
  StyledContent,
  StyledDate,
  StyledHeader,
  StyledItem,
  StyledLink,
  StyledList,
  StyledMessage,
} from './NotificationsPopover.style';

interface NotificationIconProps {
  type: string;
  theme: DefaultTheme;
}

const NotificationIcon = ({ type, theme }: NotificationIconProps) => {
  if (type === 'SUCCESS') {
    return <CheckCircleOutlined className="status-icon" style={{ color: theme.green }} />;
  }
  if (type === 'ERROR') {
    return <WarningOutlined className="status-icon" style={{ color: theme.red }} />;
  }

  return <InfoCircleOutlined className="status-icon" style={{ color: theme.teal }} />;
};

const fromNowDisplay = (date: string) => moment(date).fromNow();

interface StyledLinkItemProps {
  children: React.ReactNode;
  href: string;
  onClick: () => Promise<unknown>;
  onClose?: () => void;
  unread: boolean;
}

function toRoute(href: string): string | null {
  if (!href) {
    return null;
  }
  try {
    const { pathname, search } = new URL(href, window.location.origin);
    return `${pathname}${search}`;
  } catch {
    return null;
  }
}

function StyledLinkItem({ href, children, onClose, unread, ...props }: StyledLinkItemProps) {
  const route = toRoute(href);

  return (
    <StyledItem $hasLink={Boolean(route)} $unread={unread} {...props}>
      {route ? (
        <StyledLink to={route} replace onClick={onClose}>
          {children}
        </StyledLink>
      ) : (
        children
      )}
    </StyledItem>
  );
}

export interface NotificationsPopoverProps {
  notifications: AtlasGqlNotification[];
  onClose?: () => void;
}

export function NotificationsPopover({ notifications, onClose }: NotificationsPopoverProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [updateNotifications] = useMutation(UPDATE_NOTIFICATIONS);

  const hasUnread = notifications.some(({ isRead }) => !isRead);

  async function handleMarkAllAsRead() {
    const updatedNotifications = notifications.map(({ id }) => ({ id, isRead: true }));
    await updateNotifications({ variables: { inputs: updatedNotifications } });
  }

  async function handleMarkAsRead(id: string, isRead: boolean) {
    if (!isRead) {
      await updateNotifications({ variables: { inputs: [{ id, isRead: true }] } });
    }
  }

  return (
    <StyledContent>
      <ContentHeader>
        <StyledHeader>{t('settings.notifications', 'Notifications')}</StyledHeader>
        <Button type="link" size="small" onClick={handleMarkAllAsRead} disabled={!hasUnread}>
          {t('notifications.markAllAsRead', 'Mark all as read')}
        </Button>
      </ContentHeader>
      <StyledList>
        {notifications.map(({ message, id, isRead, type, timestamp, link }) => (
          <StyledLinkItem
            key={id}
            unread={!isRead}
            href={link ? link : ''}
            onClick={() => handleMarkAsRead(id, isRead)}
            onClose={onClose}
          >
            <NotificationIcon type={type} theme={theme} />
            <StyledMessage>
              <p data-testid="notification-message">{message}</p>
              <StyledDate>{fromNowDisplay(timestamp)}</StyledDate>
            </StyledMessage>
          </StyledLinkItem>
        ))}
      </StyledList>
    </StyledContent>
  );
}
